/* eslint-disable no-unused-vars */

/**
 * Pendo Analytics Helper
 *
 * This file provides utilities for initializing and tracking user analytics with Pendo.
 */

interface PendoVisitor {
  id: string;
  email?: string;
  full_name?: string;
  // Add any other visitor properties you want to track
}

interface PendoAccount {
  id: string;
  name?: string;
  // Add any other account properties you want to track
}

interface PendoOptions {
  visitor: PendoVisitor;
  account: PendoAccount;
}

declare global {
  interface Window {
    pendo?: {
      initialize: (options: PendoOptions) => void;
      identify: (options: PendoOptions) => void;
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      updateOptions: (options: Partial<PendoOptions>) => void;
      _q?: unknown[];
    };
  }
}

/**
 * Load the Pendo script dynamically
 */
export const loadPendoScript = (): Promise<void> => new Promise((resolve, reject) => {
    const apiKey = process.env.REACT_APP_PENDO_API_KEY;

    if (!apiKey) {
        console.warn('Pendo API key not configured. Make sure REACT_APP_PENDO_API_KEY is set in .env file and dev server is restarted.');
        reject(new Error('Pendo API key not configured'));
        return;
    }

    // Check if Pendo is already loaded
    if (window.pendo) {
        resolve();
        return;
    }

    // Initialize pendo queue
    /* eslint-disable no-underscore-dangle, no-param-reassign, no-multi-assign, prefer-template, wrap-iife */
    /* eslint-disable space-before-function-paren, prefer-rest-params, func-names, max-len */
    (function (p: any, e: Document, n: string, d: string) {
        const o: any = p[d] || {};
        p[d] = o;
        o._q = o._q || [];
        const v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
        for (let w = 0, x = v.length; w < x; ++w) {
            (function (m: string) {
                o[m] = o[m] || function () {
                    const args = Array.prototype.slice.call(arguments, 0);
                    o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args));
                };
            }(v[w]));
        }
        const y = e.createElement(n) as HTMLScriptElement;
        y.async = true;
        y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
        y.onload = () => resolve();
        y.onerror = () => reject(new Error('Failed to load Pendo script'));
        const z = e.getElementsByTagName(n)[0];
        if (z && z.parentNode) {
            z.parentNode.insertBefore(y, z);
        }
    }(window, document, 'script', 'pendo'));
    /* eslint-enable no-underscore-dangle, no-param-reassign, no-multi-assign, prefer-template, wrap-iife */
    /* eslint-enable space-before-function-paren, prefer-rest-params, func-names, max-len */
});

/**
 * Initialize Pendo with user and account information
 * This should be called after user authentication
 */
export const initializePendo = async (
    visitor: PendoVisitor,
    account: PendoAccount,
): Promise<void> => {
    try {
        // Initialize Pendo only in the production environment
        if (process.env.NODE_ENV !== 'production') {
            return;
        }

        // Load Pendo script first if not already loaded
        await loadPendoScript();

        if (!window.pendo) {
            console.warn('Pendo is not loaded after script loading');
            return;
        }

        const options: PendoOptions = {
            visitor,
            account,
        };

        window.pendo.initialize(options);
        console.log('Pendo initialized successfully');
    } catch (error) {
        console.error('Error initializing Pendo:', error);
    }
};

/**
 * Track a custom event in Pendo
 */
export const trackPendoEvent = (eventName: string, metadata?: Record<string, unknown>): void => {
    if (!window.pendo) {
        console.warn('Pendo is not loaded');
        return;
    }

    try {
        window.pendo.track(eventName, metadata);
    } catch (error) {
        console.error('Error tracking Pendo event:', error);
    }
};

/**
 * Update Pendo options (useful when switching organizations)
 */
export const updatePendoAccount = (accountId: string, accountName?: string): void => {
    if (!window.pendo) {
        console.warn('Pendo is not loaded');
        return;
    }

    try {
        window.pendo.updateOptions({
            account: {
                id: accountId,
                name: accountName,
            },
        });
    } catch (error) {
        console.error('Error updating Pendo account:', error);
    }
};
